import Button from "components/button"
import Input from "layouts/application-group/components/input"
import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import request from "request"
import styles from "./create-modal.module.scss"
import { branch as branchData, productEnum } from "utils/constants"

const Modal = ({ onClose }: { onClose: () => void }) => {
  const [formData, setFormData] = useState({
    branch: "",
    loanAmount: "",
    interestRate: "",
    duration: "",
    clientCount: "",
    product: "",
    active_ahli_bank_monthly_payment:"",
  })

  const [pending, setPending] = useState(false)
  const [validError, setValidError] = useState<{
    name: string
    value: string
  } | null>(null)

  const newGroupLoanInputs = [
    {
      title: "Amount",
      type: "integer",
      field: "loanAmount",
      step: 100,
      min: 100,
      max: 500000,
      placeholder: "min: 100 max: 500000",
      unit: "JOD",
    },
    {
      title: "Duration",
      type: "integer",
      field: "duration",
      step: 1,
      min: 1,
      max: 300,
      placeholder: "min: 1 max: 300",
      unit: "mon.",
    },
    {
      title: "Interest rate (annual)",
      type: "float",
      field: "interestRate",
      step: 0.1,
      max: 33.4,
      min: 0.1,
      placeholder: "min: 0.1 max: 33.4",
      unit: "%",
    },
    {
      title: "Branch",
      type: "select",
      hasFilterText: true,
      field: "branch",
      isArabic: true,
      options: branchData,
    },
    {
      title: "Product",
      hasFilterText: true,
      type: "select",
      field: "product",
      options: productEnum,
    },
    {
      title: "Number of clients",
      type: "integer",
      field: "clientCount",
      placeholder: "min: 2, max: 5",
      step: 1,
      min: 2,
      max: 5,
    },
  ]
  const navigate = useNavigate()

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const validateForm = () => {
    const requiredFields = [
      { key: "loanAmount", message: "Amount is required" },
      { key: "duration", message: "Duration is required" },
      { key: "interestRate", message: "Interest rate is required" },
      { key: "branch", message: "Branch is required" },
      { key: "product", message: "Product is required" },
      { key: "clientCount", message: "Client count is required" },
    ]

    for (const { key, message } of requiredFields) {
      const value = formData[key as keyof typeof formData]
      if (typeof value === "string" && !value.trim()) {
        setValidError({ name: key, value: message })
        return false
      }
    }

    setValidError(null)
    return true
  }

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!validateForm()) return

    setPending(true)
    try {
      const { clientCount, loanAmount, interestRate, product, duration } =
        formData
      const { data } = await request.post("/applications/group", {
        loaners_amount: Number(clientCount),
      })
      const applicationsIds = data.applications_ids
      const groupId = data.group_id

      await Promise.all(
        applicationsIds.map((applicationsId: number) =>
          request.put(`/applications/group/${groupId}/${applicationsId}`, {
            loanAmount,
            interestRate,
            product,
            scoring_data: {
              branch: formData.branch,
              loanDuration: duration,
              active_ahli_bank_monthly_payment: Number(formData.active_ahli_bank_monthly_payment),
            },
          }),
        ),
      )

      navigate(`/application-group/${groupId}`)
    } catch (e: any) {
      toast.error(e.response.message)
    } finally {
      setPending(false)
    }
  }

  return (
    <div className={styles.modal} onMouseDown={onClose}>
      <form
        className={styles.modalWrap}
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={handleCreate}
      >
        <div className={styles.title_inputs}>
          <div className={styles.title}>New group application</div>
          <div className={styles.inputs}>
            {newGroupLoanInputs.map(
              ({ title, type, field, ...props }, index) => (
                <Input
                  key={index}
                  id={field}
                  title={title}
                  type={type as any}
                  value={formData[field as keyof typeof formData]}
                  onInput={(value: string) => {
                    handleChange(field, value.toString())
                  }}
                  options={props.options}
                  step={props.step}
                  onSelect={(selectedOption: string) => {
                    handleChange(field, selectedOption)
                  }}
                  max={props.max}
                  min={props.min}
                  placeholder={props?.placeholder}
                  error={
                    validError?.name === field ? validError.value : undefined
                  }
                />
              ),
            )}
          </div>
        </div>

        <div className={styles.buttonGroup}>
          <Button color="orange" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="green" pending={pending}>
            Create
          </Button>
        </div>
      </form>
    </div>
  )
}

export default Modal
